// Lot 2b — Section « Mon profil de force » du Profil : radar 5 axes + détail par mouvement.
//
// ⚠️ RÈGLE PRODUIT IMPÉRATIVE — le polygone en pointillé représente l'objectif du client et
// s'appelle « Cible » (ou « Cible du bloc »), point. Les mots Débutant / Intermédiaire /
// Avancé désignent déjà le niveau de programme du client ailleurs dans l'app (il change tous
// les 12 semaines) : les afficher ici induirait le client en erreur sur son propre niveau.
// Aucun de ces trois mots ne doit apparaître dans ce fichier, ni dans les libellés qu'il rend.
// Les paliers des tables sont manipulés sous leurs noms ExRx (novice/intermediate) et ne sont
// de toute façon jamais affichés.

import { useMemo, useState } from "react"
import { motion } from "framer-motion"
import { Activity, Info, PartyPopper } from "lucide-react"
import type { StrengthRadarData, StrengthAxisEntry } from "@/lib/strengthTargets"
import { SectionTitle, SectionCaption } from "@/components/profile/ui"
import type { TestingSchedule } from "@/hooks/useTestingSchedule"
import { formatDateShortFr } from "@/lib/utils"
import {
  MOVEMENT_LABELS,
  MOVEMENT_SHORT_LABELS,
  GENDER_SOURCE_LABEL,
  PULL_UP_SOURCE_NOTE,
  pullUpTotalFromRatio,
  pullUpAddedLoadFromRatio,
} from "@/lib/strengthStandards"

interface Props {
  /** Chargé par `useStrengthRadar` côté ProfilePage — la liste des records s'en sert aussi. */
  data: StrengthRadarData | null
  loading: boolean
  /**
   * Calendrier des tests du bloc, partagé avec « Mes mesures » : un seul rythme pour les
   * deux sections, celui de `programs.testing_sessions`.
   */
  schedule: TestingSchedule
}

// ── Palette ─────────────────────────────────────────────────
// Deux séries seulement. Couples validés sur la surface sombre des cartes du Profil
// (#0b1220) : bande de clarté OKLCH, plancher de chroma, séparation daltonienne
// (ΔE 20.1 protan / 25.0 tritan) et contraste ≥ 3:1 — tous au vert. L'identité ne
// repose pas sur la couleur seule : trait plein vs pointillé, plus une légende et le
// détail chiffré axe par axe juste en dessous.
const CURRENT_COLOR = "#0e9fc0"
const TARGET_COLOR = "#c98105"

// ── Géométrie du radar ──────────────────────────────────────
// Le viewBox est plus large que le radar lui-même : les libellés des deux sommets
// latéraux dépassent le cercle des axes (« Traction » jusqu'à x ≈ -13, « Soulevé »
// jusqu'à x ≈ 307 pour un rayon de 92). Une boîte 0→300 les rognait. Marge symétrique
// autour de x = 150 pour que le radar reste centré dans la carte.
const VIEWBOX_MIN_X = -20
const VIEWBOX_MIN_Y = 0
const VIEWBOX_WIDTH = 340
const VIEWBOX_HEIGHT = 250
const VIEWBOX = `${VIEWBOX_MIN_X} ${VIEWBOX_MIN_Y} ${VIEWBOX_WIDTH} ${VIEWBOX_HEIGHT}`
const CENTER = { x: 150, y: 132 }
const MAX_RADIUS = 92
/** La cible est posée sur un anneau de la grille : le pointillé ne paraît pas arbitraire. */
const TARGET_RING = 0.75
const GRID_RINGS = [0.25, 0.5, 0.75, 1]

/**
 * Écart minimal (en unité de rayon, avant multiplication par MAX_RADIUS) entre le tracé
 * « Actuel » et l'anneau « Cible » pour que les deux polygones restent visuellement
 * distinguables. À TARGET_RING fixe (0,75) et MAX_RADIUS=92, un client à 90-95 % de sa
 * cible rend un écart naturel d'environ 4 à 7 px — quasi indissociable avec deux traits de
 * 2 px chacun. En dessous de ce seuil, le rayon AFFICHÉ du point « Actuel » est repoussé
 * (vers le centre s'il est en retrait de la cible, vers l'extérieur s'il la dépasse) pour
 * retrouver cet écart minimal. Le pourcentage textuel affiché à côté de chaque axe reste
 * calculé sur la vraie valeur (`ratioOfTarget`), jamais sur ce rayon retouché : aucune
 * information n'est perdue, seule la lecture visuelle du graphique est clarifiée. Un axe
 * déjà bien séparé (ex. développé couché/militaire à 87 %, écart naturel ~9 px) n'est pas
 * retouché — 0,075 × 92 ≈ 6,9 px, sous les ~9 px de ce cas.
 */
const MIN_VISUAL_GAP_RATIO = 0.075

function axisAngle(index: number, total: number): number {
  return -Math.PI / 2 + (index * 2 * Math.PI) / total
}

function pointAt(index: number, total: number, radiusRatio: number) {
  const angle = axisAngle(index, total)
  const r = MAX_RADIUS * radiusRatio
  return { x: CENTER.x + r * Math.cos(angle), y: CENTER.y + r * Math.sin(angle) }
}

function polygonPoints(total: number, radiusRatios: number[] | number): string {
  return Array.from({ length: total }, (_, i) => {
    const ratio = typeof radiusRatios === "number" ? radiusRatios : radiusRatios[i]
    const p = pointAt(i, total, ratio)
    return `${p.x.toFixed(2)},${p.y.toFixed(2)}`
  }).join(" ")
}

// ── Formatage ───────────────────────────────────────────────

/** Toutes les charges s'affichent au kilo entier (consigne du brief). */
function formatKg(value: number): string {
  return `${Math.round(value)} kg`
}

function formatRatio(value: number): string {
  return `×${value.toFixed(2).replace(".", ",")}`
}

function formatPercent(ratio: number): string {
  return `${Math.round(ratio * 100)} %`
}

/**
 * Charge d'une traction telle qu'elle s'affiche : la charge TOTALE en kg (l'unité dans
 * laquelle le client saisit son 1RM — cf. la sémantique de `rm_pull_up`), suivie du lest
 * que cela représente au-dessus du poids de corps. Un lest négatif s'affiche comme une
 * assistance. Le pourcentage de cible, lui, reste calculé sur le ratio.
 */
function describePullUp(ratio: number, bodyweightKg: number): string {
  const total = pullUpTotalFromRatio(ratio, bodyweightKg)
  const added = pullUpAddedLoadFromRatio(ratio, bodyweightKg)
  if (total == null || added == null) return formatRatio(ratio)
  const rounded = Math.round(added)
  const suffix =
    rounded === 0 ? "poids du corps" : rounded > 0 ? `dont +${rounded} kg` : `assistance ${Math.abs(rounded)} kg`
  return `${formatKg(total)} · ${suffix}`
}

function describeCurrent(entry: StrengthAxisEntry, bodyweightKg: number): string | null {
  if (entry.currentValue == null) return null
  if (entry.movement !== "traction_lestee") return formatKg(entry.currentValue)
  return describePullUp(entry.currentValue, bodyweightKg)
}

function describeTarget(entry: StrengthAxisEntry, bodyweightKg: number): string | null {
  if (entry.targetValue == null) return null
  if (entry.movement !== "traction_lestee") return formatKg(entry.targetValue)
  const total = pullUpTotalFromRatio(entry.targetValue, bodyweightKg)
  return total == null ? formatRatio(entry.targetValue) : formatKg(total)
}

/**
 * Repousse le rayon affiché d'un point « Actuel » quand il est trop proche de l'anneau
 * « Cible » (voir MIN_VISUAL_GAP_RATIO). Un ratio de 0 (axe sans donnée) n'est jamais
 * repoussé : l'écart à TARGET_RING y est déjà maximal (0,75), bien au-dessus du seuil.
 */
function separateFromTargetRing(ratio: number): number {
  const diff = ratio - TARGET_RING
  if (Math.abs(diff) >= MIN_VISUAL_GAP_RATIO) return ratio
  const nudged = diff <= 0 ? TARGET_RING - MIN_VISUAL_GAP_RATIO : TARGET_RING + MIN_VISUAL_GAP_RATIO
  return Math.max(0, Math.min(nudged, 1))
}

/** Position (en % du viewBox) d'un point du radar, pour ancrer un élément HTML dessus. */
function pointToPercent(p: { x: number; y: number }): { leftPct: number; topPct: number } {
  return {
    leftPct: ((p.x - VIEWBOX_MIN_X) / VIEWBOX_WIDTH) * 100,
    topPct: ((p.y - VIEWBOX_MIN_Y) / VIEWBOX_HEIGHT) * 100,
  }
}

/**
 * Transform CSS ancrant l'encart sur le point plutôt que de le centrer aveuglément dessus :
 * flip horizontal/vertical selon le quadrant pour que l'encart reste dans la carte (qui a
 * overflow-hidden) même pour un point proche d'un bord, plus un petit décalage en pixels
 * pour ne pas recouvrir le point lui-même.
 */
function tooltipTransform(leftPct: number, topPct: number): string {
  const x = leftPct < 30 ? "0%" : leftPct > 70 ? "-100%" : "-50%"
  const xPx = leftPct < 30 ? 10 : leftPct > 70 ? -10 : 0
  const y = topPct < 30 ? "0%" : "-100%"
  const yPx = topPct < 30 ? 12 : -12
  return `translate(calc(${x} + ${xPx}px), calc(${y} + ${yPx}px))`
}

interface TooltipInfo {
  leftPct: number
  topPct: number
  current: string
  percentLabel: string
}

/**
 * Contenu de l'encart au tap sur un point : uniquement la valeur actuelle et le
 * pourcentage de la cible atteint — le nom du mouvement est déjà le libellé de l'axe
 * juste à côté, le répéter serait redondant.
 */
function buildTooltipInfo(
  data: StrengthRadarData,
  currentRatios: number[],
  total: number,
  axisIndex: number
): TooltipInfo | null {
  const entry = data.entries[axisIndex]
  if (!entry || entry.ratioOfTarget == null) return null
  const ratio = currentRatios[axisIndex]
  if (ratio <= 0) return null
  const current = describeCurrent(entry, data.bodyweightKg)
  if (current == null) return null
  const { leftPct, topPct } = pointToPercent(pointAt(axisIndex, total, ratio))
  return { leftPct, topPct, current, percentLabel: `${formatPercent(entry.ratioOfTarget)} de la cible` }
}

/**
 * Ligne de repère temporel sous l'en-tête : de quand datent les valeurs peintes sur le
 * radar, et à quelle séance tombe le prochain test.
 *
 * La date vient de `rm_history` (via le calendrier partagé) et non d'`athlete_profiles`,
 * dont `rm_test_date` est réécrite à chaque correction d'un seul mouvement.
 *
 * L'échéance est exprimée en NUMÉRO DE SÉANCE, l'unité dans laquelle le client la
 * rencontre dans son programme, avec la semaine entre parenthèses quand la correspondance
 * est connue. C'est le même calendrier que celui de « Mes mesures ».
 */
function strengthCaption(schedule: TestingSchedule): string {
  const head = schedule.lastRmDate
    ? `Dernier test le ${formatDateShortFr(schedule.lastRmDate)}`
    : "Aucun test enregistré"

  if (schedule.nextTestSession == null) return head
  const week = schedule.nextTestWeek != null ? ` (semaine ${schedule.nextTestWeek})` : ""
  return `${head} · prochain test à la séance ${schedule.nextTestSession}${week}`
}

// ── Composant ───────────────────────────────────────────────

export const StrengthRadarSection = ({ data, loading, schedule }: Props) => {
  const [activeAxis, setActiveAxis] = useState<number | null>(null)

  const geometry = useMemo(() => {
    if (!data) return null
    const total = data.entries.length
    // Un axe sans cible ni record retombe au centre : le creux dans le polygone dit
    // « rien de mesuré ici », ce qui est l'information juste.
    const currentRatios = data.entries.map((e) => {
      if (e.ratioOfTarget == null) return 0
      // Le tracé est borné à l'anneau extérieur ; le pourcentage affiché, lui, reste exact.
      return separateFromTargetRing(Math.min(e.ratioOfTarget * TARGET_RING, 1))
    })
    return {
      total,
      gridPolygons: GRID_RINGS.map((r) => polygonPoints(total, r)),
      targetPolygon: polygonPoints(total, TARGET_RING),
      currentPolygon: polygonPoints(total, currentRatios),
      currentRatios,
    }
  }, [data])

  if (loading) {
    return (
      <div className="px-4 mt-6 relative z-10">
        <SectionTitle label="Mon profil de force" />
        <div className="bg-gray-900/70 border border-gray-800/60 rounded-2xl p-4">
          <p className="text-[12px] text-gray-500 text-center py-8">Chargement…</p>
        </div>
      </div>
    )
  }

  if (!data) return null
  if (!geometry) return null

  const tooltip = activeAxis != null
    ? buildTooltipInfo(data, geometry.currentRatios, geometry.total, activeAxis)
    : null

  const sourceLine = data.bodyweightBand
    ? `Références ExRx · ${GENDER_SOURCE_LABEL[data.gender]} ${data.bodyweightBand} kg · 1RM`
    : `Références ExRx · ${GENDER_SOURCE_LABEL[data.gender]} · 1RM`

  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.17 }}
      className="px-4 mt-6 relative z-10"
    >
      <SectionTitle label="Mon profil de force" />
      {!schedule.loading && <SectionCaption>{strengthCaption(schedule)}</SectionCaption>}

      <div
        className="bg-gray-900/70 border border-gray-800/60 rounded-2xl overflow-hidden"
        onClick={() => setActiveAxis(null)}
      >
        {/* Félicitations — test final au-dessus de la cible */}
        {data.exceeded.length > 0 && (
          <div
            className="flex items-start gap-2.5 px-4 py-3"
            style={{ background: "rgba(201,129,5,0.10)", borderBottom: "1px solid rgba(201,129,5,0.20)" }}
          >
            <PartyPopper className="w-4 h-4 shrink-0 mt-0.5" style={{ color: TARGET_COLOR }} />
            <p className="text-[12px] text-gray-300 leading-relaxed">
              Cible dépassée sur {data.exceeded.map((m) => MOVEMENT_LABELS[m].toLowerCase()).join(", ")}.
              {" "}Le prochain bloc repart de ce nouveau record.
            </p>
          </div>
        )}

        {/* Radar */}
        <div className="pt-4 px-2 relative">
          <svg viewBox={VIEWBOX} className="w-full h-auto" role="img"
            aria-label="Radar de force : valeurs actuelles comparées à la cible du bloc">
            {geometry && (
              <>
                {/* Grille — volontairement discrète */}
                {geometry.gridPolygons.map((pts, i) => (
                  <polygon key={i} points={pts} fill="none" stroke="rgba(148,163,184,0.13)" strokeWidth={1} />
                ))}
                {data.entries.map((_, i) => {
                  const p = pointAt(i, geometry.total, 1)
                  return (
                    <line key={i} x1={CENTER.x} y1={CENTER.y} x2={p.x} y2={p.y}
                      stroke="rgba(148,163,184,0.13)" strokeWidth={1} />
                  )
                })}

                {/* Cible du bloc — pointillé */}
                <polygon points={geometry.targetPolygon} fill="none" stroke={TARGET_COLOR}
                  strokeWidth={2} strokeDasharray="5 4" strokeLinejoin="round" />

                {/* Valeurs actuelles — plein */}
                <polygon points={geometry.currentPolygon} fill={`${CURRENT_COLOR}2e`}
                  stroke={CURRENT_COLOR} strokeWidth={2} strokeLinejoin="round" />

                {/* Sommets : anneau de surface de 2px pour ne pas se fondre dans le remplissage.
                    Chaque point est tapable (encart avec la valeur actuelle et le
                    pourcentage de la cible atteint — le nom du mouvement est déjà le
                    libellé de l'axe, pas besoin de le répéter). Le cercle visible reste à
                    r=4 ; un cercle transparent plus grand (r=11) porte le tap/clic pour une
                    cible tactile confortable sans agrandir le rendu visuel. */}
                {geometry.currentRatios.map((ratio, i) => {
                  if (ratio <= 0) return null
                  const p = pointAt(i, geometry.total, ratio)
                  const isActive = activeAxis === i
                  const toggle = () => setActiveAxis(isActive ? null : i)
                  return (
                    <g key={i}>
                      {isActive && (
                        <circle cx={p.x} cy={p.y} r={7} fill="none" stroke={CURRENT_COLOR}
                          strokeWidth={1.5} opacity={0.55} pointerEvents="none" />
                      )}
                      <circle cx={p.x} cy={p.y} r={4} fill={CURRENT_COLOR}
                        stroke="#0b1220" strokeWidth={2} pointerEvents="none" />
                      <circle
                        cx={p.x} cy={p.y} r={11} fill="transparent" pointerEvents="all"
                        role="button" tabIndex={0} aria-pressed={isActive}
                        aria-label={`${MOVEMENT_LABELS[data.entries[i].movement]} — afficher le détail`}
                        style={{ cursor: "pointer" }}
                        onClick={(e) => { e.stopPropagation(); toggle() }}
                        onKeyDown={(e) => {
                          if (e.key !== "Enter" && e.key !== " ") return
                          e.preventDefault(); e.stopPropagation(); toggle()
                        }}
                      />
                    </g>
                  )
                })}

                {/* Libellés d'axe */}
                {data.entries.map((entry, i) => {
                  const p = pointAt(i, geometry.total, 1.28)
                  const angle = axisAngle(i, geometry.total)
                  const cos = Math.cos(angle)
                  const anchor = Math.abs(cos) < 0.3 ? "middle" : cos > 0 ? "start" : "end"
                  return (
                    <text key={i} x={p.x} y={p.y} textAnchor={anchor} dominantBaseline="middle"
                      className="fill-gray-400" style={{ fontSize: 11, fontWeight: 600 }}>
                      {MOVEMENT_SHORT_LABELS[entry.movement]}
                    </text>
                  )
                })}
              </>
            )}
          </svg>

          {tooltip && (
            <div
              className="absolute pointer-events-none rounded-lg border px-2.5 py-1.5 text-[11px] leading-snug whitespace-nowrap shadow-lg"
              style={{
                left: `${tooltip.leftPct}%`,
                top: `${tooltip.topPct}%`,
                transform: tooltipTransform(tooltip.leftPct, tooltip.topPct),
                background: "#0f172aef",
                borderColor: "rgba(14,159,192,0.35)",
              }}
            >
              <div className="font-bold text-white">{tooltip.current}</div>
              <div className="text-gray-400 mt-0.5">{tooltip.percentLabel}</div>
            </div>
          )}
        </div>

        {/* Légende — deux séries, jamais la couleur seule */}
        <div className="flex items-center justify-center gap-5 px-4 pb-3.5 pt-1">
          <div className="flex items-center gap-2">
            <svg width="18" height="8" aria-hidden="true">
              <line x1="0" y1="4" x2="18" y2="4" stroke={CURRENT_COLOR} strokeWidth={2} />
            </svg>
            <span className="text-[11px] text-gray-400">Actuel</span>
          </div>
          <div className="flex items-center gap-2">
            <svg width="18" height="8" aria-hidden="true">
              <line x1="0" y1="4" x2="18" y2="4" stroke={TARGET_COLOR} strokeWidth={2} strokeDasharray="5 4" />
            </svg>
            <span className="text-[11px] text-gray-400">Cible du bloc</span>
          </div>
        </div>

        {/* Détail par axe — la vue « tableau » du radar */}
        <div style={{ borderTop: "1px solid rgba(30,41,59,0.4)" }}>
          {data.entries.map((entry, i, arr) => {
            const current = describeCurrent(entry, data.bodyweightKg)
            const target = describeTarget(entry, data.bodyweightKg)
            return (
              <div key={entry.movement} className="flex items-center gap-3 px-4 py-3"
                style={{ borderBottom: i < arr.length - 1 ? "1px solid rgba(30,41,59,0.3)" : "none" }}>
                <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                  style={{ background: "rgba(14,159,192,0.10)", border: "1px solid rgba(14,159,192,0.20)" }}>
                  <Activity className="w-4 h-4" style={{ color: CURRENT_COLOR }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] text-gray-300">{MOVEMENT_LABELS[entry.movement]}</div>
                  <div className="text-[11px] text-gray-500 mt-0.5">
                    {current == null
                      ? "Non testé"
                      : entry.ratioOfTarget != null
                        ? `${current} · ${formatPercent(entry.ratioOfTarget)} de la cible`
                        : `${current} · cible au prochain test`}
                  </div>
                </div>
                {target && (
                  <div className="text-right shrink-0">
                    <div className="text-[10px] text-gray-600 uppercase tracking-wider">Cible</div>
                    <div className="text-[13px] font-bold" style={{ color: TARGET_COLOR }}>{target}</div>
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {/* Mention de source */}
        <div className="px-4 py-3 space-y-1.5" style={{ borderTop: "1px solid rgba(30,41,59,0.4)" }}>
          <p className="text-[10px] text-gray-600 leading-relaxed">{sourceLine}</p>
          <p className="text-[10px] text-gray-600 leading-relaxed">{PULL_UP_SOURCE_NOTE}</p>
          {data.bracketClamped && (
            <p className="text-[10px] text-gray-600 leading-relaxed flex items-start gap-1.5">
              <Info className="w-3 h-3 shrink-0 mt-0.5" />
              Ton âge sort des tranches couvertes par les tables : les repères affichés utilisent
              la tranche la plus proche.
            </p>
          )}
          {!data.hasTargets && (
            <p className="text-[10px] text-gray-600 leading-relaxed">
              La cible du bloc se calcule à ta séance de test. Elle apparaîtra ici juste après.
            </p>
          )}
        </div>
      </div>
    </motion.div>
  )
}
